/**
 * Is a policy still GOOD? There is no draft/review/published lifecycle in
 * this workspace — a policy in the library is assumed published — so the
 * one status worth showing is freshness: current, or out of date because a
 * review lapsed, a regulatory change touched it, a finding on it is still
 * open, nobody has claimed it, or the document never processed.
 *
 * One rule, shared by the policy page's pill, the library's status column and
 * the library's freshness chart, so the three can never disagree.
 *
 * @module components/regulation/policy/policyFreshness
 */
import type { OrgPolicyGovernance } from "../../../state/governance";
import type { Policy } from "../../../state/policies";

export type Freshness =
  | "current"
  | "review-overdue"
  | "out-of-date"
  | "open-findings"
  | "not-governed"
  | "processing"
  | "failed";

/** Fixed order: the chart's slices and legend never re-sort by count. */
export const FRESHNESS_ORDER: ReadonlyArray<Freshness> = [
  "current",
  "review-overdue",
  "out-of-date",
  "failed",
  "processing",
  "open-findings",
  "not-governed",
];

export const FRESHNESS_LABELS: Record<Freshness, string> = {
  current: "Up to date",
  "review-overdue": "Review overdue",
  "out-of-date": "Out of date",
  "open-findings": "Open findings",
  "not-governed": "Not yet checked",
  processing: "Processing",
  failed: "Processing failed",
};

export const FRESHNESS_PILL: Record<Freshness, string> = {
  current: "bg-success/10 text-success",
  "review-overdue": "bg-warning/12 text-warning",
  "out-of-date": "bg-destructive/10 text-destructive",
  "open-findings": "bg-warning/12 text-warning",
  "not-governed": "bg-muted text-muted-foreground",
  processing: "bg-primary/10 text-primary",
  failed: "bg-destructive/10 text-destructive",
};

/**
 * Status colours, as theme tokens so they follow light/dark. Validated with
 * the dataviz palette checks (adjacent-pair CVD separation passes; identity
 * never rides on colour alone — every slice is named and counted beside it).
 * "Failed" shares the destructive hue with "Out of date" and is darkened so
 * the two never sit as one wedge.
 */
export const FRESHNESS_COLORS: Record<Freshness, string> = {
  current: "var(--color-success)",
  "review-overdue": "var(--color-warning)",
  "out-of-date": "var(--color-destructive)",
  // An orange between warning and destructive. Its neighbours in
  // FRESHNESS_ORDER are "Processing" (primary) and "Not yet checked" (muted),
  // never the amber or red slices it sits between in hue.
  "open-findings": "color-mix(in oklab, var(--color-warning) 70%, var(--color-destructive))",
  "not-governed": "var(--color-muted-foreground)",
  processing: "var(--color-primary)",
  failed: "color-mix(in oklab, var(--color-destructive) 78%, black)",
};

/**
 * The governance facts freshness reads. A row that exists only to carry a
 * title (no owner, no review cycle) is not governance -- nobody has claimed
 * the document -- so ownership and cadence are part of the input.
 */
export type FreshnessGovernance = Pick<OrgPolicyGovernance, "reviewDueAt" | "reviewFrequency"> & {
  readonly ownerId?: string | null;
  readonly ownerEmail?: string | null;
};

export const isGoverned = (governance: FreshnessGovernance | null | undefined): boolean =>
  governance !== null &&
  governance !== undefined &&
  (governance.ownerId != null || governance.ownerEmail != null || governance.reviewFrequency !== "none");

export interface FreshnessInput {
  readonly policy: Pick<Policy, "id" | "status">;
  /** The policy's governance row, or null/undefined when nobody governed it. */
  readonly governance: FreshnessGovernance | null | undefined;
  /** True when a regulatory update names this policy in `affectedPolicyIds`. */
  readonly touchedByChange: boolean;
  /**
   * Findings on the document still waiting on a decision (regulatory findings
   * plus gap suggestions — see `openWorkByPolicy`). Omitted reads as none.
   */
  readonly openFindings?: number;
  readonly now: number;
}

export function policyFreshness({
  policy,
  governance,
  touchedByChange,
  openFindings = 0,
  now,
}: FreshnessInput): Freshness {
  if (policy.status === "failed") return "failed";
  if (policy.status === "processing") return "processing";
  // A regulatory change that names this policy makes it out of date until
  // someone amends it; a lapsed review is the same signal from the calendar.
  if (touchedByChange) return "out-of-date";
  const due = governance?.reviewDueAt ? Date.parse(governance.reviewDueAt) : Number.NaN;
  if (!Number.isNaN(due) && due < now) return "review-overdue";
  // An open finding is a gap someone has to decide: the document is not
  // "up to date" while it waits, and it has been checked — the finding is
  // the check's result — so it is not "not yet checked" either.
  if (openFindings > 0) return "open-findings";
  // No owner, no review cycle: nobody has claimed this document yet, and
  // "up to date" would imply someone checked.
  if (!isGoverned(governance)) return "not-governed";
  return "current";
}

export interface FreshnessSlice {
  readonly key: Freshness;
  readonly label: string;
  readonly count: number;
  readonly color: string;
}

/** Counts per freshness in fixed order, empty buckets dropped (no zero-width wedges). */
export function freshnessSlices(
  policies: ReadonlyArray<Pick<Policy, "id" | "status">>,
  governance: ReadonlyMap<string, FreshnessGovernance>,
  touchedPolicyIds: ReadonlySet<string>,
  now: number,
  /** Open findings per document; without it no document reads as having any. */
  openFindings?: ReadonlyMap<string, number>,
): ReadonlyArray<FreshnessSlice> {
  const counts = new Map<Freshness, number>();
  for (const policy of policies) {
    const key = policyFreshness({
      policy,
      governance: governance.get(policy.id) ?? null,
      touchedByChange: touchedPolicyIds.has(policy.id),
      openFindings: openFindings?.get(policy.id) ?? 0,
      now,
    });
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return FRESHNESS_ORDER.map((key) => ({
    key,
    label: FRESHNESS_LABELS[key],
    count: counts.get(key) ?? 0,
    color: FRESHNESS_COLORS[key],
  })).filter((slice) => slice.count > 0);
}
